import { MouseEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import parse from 'html-react-parser';

import { bridgeAtom, isFontsLoadedAtom, useValue } from '@/store';

import { useTheme } from '@/modules/common';

import { Background, CloseButton, Content, Crest, Star, Stars, Title, Tooltip, Wrapper } from './Default.styles';

interface DefaultWidgetProps {
  widgetTitle: string;
  closeBtn: boolean;
  isTooltipOpen: boolean;
  tooltipText: string;
  toggleHover: (state: boolean) => void;
  onTooltipHided: () => void;
  closeInitTooltip: (e: MouseEvent) => void;
  onClick: () => void;
}

export const DefaultLayout = ({
  isTooltipOpen,
  tooltipText,
  onTooltipHided,
  toggleHover,
  closeBtn,
  closeInitTooltip,
  onClick,
}: DefaultWidgetProps) => {
  const { isRtl } = useTheme();
  const [isReady, setIsReady] = useState(false);
  const [width, setWidth] = useState<number>(60);

  const ignoreHoverRef = useRef<boolean>(false);

  const isFontsLoaded = useValue(isFontsLoadedAtom);
  const { translations } = useValue(bridgeAtom);

  const isAnimate = useRef(false);
  const titleRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const titleWidthRef = useRef<number>(0);
  const tooltipWidthRef = useRef<number>(0);
  const isFirstRenderRef = useRef(true);

  const widgetTitle = translations?.widgetTitle?.length ? translations.widgetTitle : translations.widget?.title;

  const onAnimationEnd = useCallback(
    (event: React.TransitionEvent) => {
      const currentOpacity = Number(window.getComputedStyle(event.target as HTMLElement).opacity);
      if (currentOpacity !== 0) {
        return;
      }

      if (isFirstRenderRef.current) {
        isFirstRenderRef.current = false;
      }
      onTooltipHided();
    },
    [onTooltipHided]
  );

  const onMouseEnter = useCallback(() => toggleHover(true), [toggleHover]);

  const onMouseLeave = useCallback(() => {
    ignoreHoverRef.current = false;
    toggleHover(false);
  }, [toggleHover]);

  useLayoutEffect(() => {
    if (!tooltipRef.current || !titleRef.current) {
      return;
    }

    titleWidthRef.current = titleRef.current.getBoundingClientRect().width;
    tooltipWidthRef.current = tooltipRef.current.getBoundingClientRect().width;
    setIsReady(true);
  }, [isFontsLoaded]);

  useEffect(() => {
    if (!tooltipRef.current) {
      return;
    }

    tooltipWidthRef.current = tooltipRef.current.getBoundingClientRect().width;
  }, [tooltipText]);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    setWidth(titleWidthRef.current);
  }, [isReady]);

  useEffect(() => {
    setWidth(isTooltipOpen ? tooltipWidthRef.current : titleWidthRef.current);
  }, [isTooltipOpen, closeBtn]);

  if (!isFontsLoaded) {
    return null;
  }

  return (
    <Wrapper onClick={onClick} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <Content width={width} isReady={isReady}>
        <Title ref={titleRef} isVisible={!isTooltipOpen}>
          <Stars isRtl={isRtl}>
            <Star />
            <Star />
          </Stars>

          <p>{parse(widgetTitle)}</p>
        </Title>

        <Tooltip ref={tooltipRef} isVisible={isTooltipOpen} onTransitionEnd={onAnimationEnd}>
          {closeBtn && (
            <CloseButton onClick={closeInitTooltip}>
              <Crest />
            </CloseButton>
          )}
          <p>{parse(tooltipText)}</p>
        </Tooltip>
      </Content>

      <Background isTooltipOpen={isTooltipOpen} isAnimate={isAnimate.current} />
    </Wrapper>
  );
};
